import { useEffect, useRef } from "react";
import { useVisitorTracking } from "@/lib/queries/visitor";
import type {
  VisitorData,
  ConnectionInfo,
  ScreenInfo,
  ViewportInfo,
  PerformanceInfo,
  MediaCapabilities,
  TouchInfo,
  UTMParams,
  LocationInfo,
  DocumentInfo,
  BatteryInfo,
} from "@/lib/api/visitor";

// Helper to generate a simple session ID
function generateSessionId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
}

// Helper to get or create visitor ID for tracking repeat visits
function getOrCreateVisitorId(): {
  visitorId: string;
  isNew: boolean;
  visitCount: number;
  previousVisit?: string;
} {
  const storageKey = "koricode_visitor_id";
  const countKey = "koricode_visit_count";
  const lastVisitKey = "koricode_last_visit";

  let visitorId = localStorage.getItem(storageKey);
  let visitCount = parseInt(localStorage.getItem(countKey) || "0", 10);
  const previousVisit = localStorage.getItem(lastVisitKey) || undefined;
  const isNew = !visitorId;

  if (!visitorId) {
    visitorId = `v_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem(storageKey, visitorId);
  }

  visitCount += 1;
  localStorage.setItem(countKey, visitCount.toString());
  localStorage.setItem(lastVisitKey, new Date().toISOString());

  return { visitorId, isNew, visitCount, previousVisit };
}

// Get connection info
function getConnectionInfo(): ConnectionInfo | undefined {
  const nav = navigator as any;
  const conn = nav.connection || nav.mozConnection || nav.webkitConnection;

  if (!conn) return undefined;

  return {
    effectiveType: conn.effectiveType,
    downlink: conn.downlink,
    rtt: conn.rtt,
    saveData: conn.saveData,
    type: conn.type,
  };
}

// Get screen info
function getScreenInfo(): ScreenInfo {
  const screen = window.screen;
  const orientation = screen.orientation;

  return {
    width: screen.width,
    height: screen.height,
    availWidth: screen.availWidth,
    availHeight: screen.availHeight,
    colorDepth: screen.colorDepth,
    pixelDepth: screen.pixelDepth,
    orientation: orientation?.type,
    orientationAngle: orientation?.angle,
  };
}

// Get viewport info
function getViewportInfo(): ViewportInfo {
  return {
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    outerWidth: window.outerWidth,
    outerHeight: window.outerHeight,
    scrollX: window.scrollX,
    scrollY: window.scrollY,
  };
}

// Get performance info
function getPerformanceInfo(): PerformanceInfo | undefined {
  try {
    const perf = window.performance;
    if (!perf) return undefined;

    const timing = perf.timing;
    const navStart = timing.navigationStart;

    // Get paint entries
    const paintEntries = perf.getEntriesByType?.("paint") || [];
    const firstPaint = paintEntries.find((e) => e.name === "first-paint");
    const fcp = paintEntries.find((e) => e.name === "first-contentful-paint");

    return {
      pageLoadTime:
        timing.loadEventEnd > 0 ? timing.loadEventEnd - navStart : undefined,
      domContentLoaded:
        timing.domContentLoadedEventEnd > 0
          ? timing.domContentLoadedEventEnd - navStart
          : undefined,
      firstPaint: firstPaint?.startTime,
      firstContentfulPaint: fcp?.startTime,
      dnsLookup: timing.domainLookupEnd - timing.domainLookupStart,
      tcpConnection: timing.connectEnd - timing.connectStart,
      serverResponse: timing.responseEnd - timing.requestStart,
      domProcessing: timing.domComplete - timing.domLoading,
      resourceLoading: timing.loadEventStart - timing.domContentLoadedEventEnd,
    };
  } catch {
    return undefined;
  }
}

// Get media capabilities
function getMediaCapabilities(): MediaCapabilities {
  const nav = navigator as any;

  // Check WebGL support
  let webglSupported = false;
  let webgl2Supported = false;
  let webglRenderer: string | undefined;
  let webglVendor: string | undefined;

  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (gl) {
      webglSupported = true;
      const debugInfo = (gl as WebGLRenderingContext).getExtension(
        "WEBGL_debug_renderer_info",
      );
      if (debugInfo) {
        webglRenderer = (gl as WebGLRenderingContext).getParameter(
          debugInfo.UNMASKED_RENDERER_WEBGL,
        );
        webglVendor = (gl as WebGLRenderingContext).getParameter(
          debugInfo.UNMASKED_VENDOR_WEBGL,
        );
      }
    }
    const gl2 = canvas.getContext("webgl2");
    webgl2Supported = !!gl2;
  } catch {
    // WebGL not supported
  }

  return {
    pdfViewerEnabled: nav.pdfViewerEnabled ?? false,
    webglSupported,
    webglRenderer,
    webglVendor,
    webgl2Supported,
    canvasSupported: !!document.createElement("canvas").getContext,
    audioContextSupported: !!(
      window.AudioContext || (window as any).webkitAudioContext
    ),
    webRtcSupported: !!(
      window.RTCPeerConnection || (window as any).webkitRTCPeerConnection
    ),
    serviceWorkerSupported: "serviceWorker" in navigator,
    webSocketSupported: "WebSocket" in window,
    localStorageSupported: (() => {
      try {
        return !!localStorage;
      } catch {
        return false;
      }
    })(),
    sessionStorageSupported: (() => {
      try {
        return !!sessionStorage;
      } catch {
        return false;
      }
    })(),
    indexedDbSupported: !!window.indexedDB,
    cookiesEnabled: navigator.cookieEnabled,
  };
}

// Get touch info
function getTouchInfo(): TouchInfo {
  return {
    touchSupported: "ontouchstart" in window || navigator.maxTouchPoints > 0,
    maxTouchPoints: navigator.maxTouchPoints || 0,
    pointerSupported: "PointerEvent" in window,
  };
}

// Get UTM parameters
function getUTMParams(): UTMParams {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") || undefined,
    utm_medium: params.get("utm_medium") || undefined,
    utm_campaign: params.get("utm_campaign") || undefined,
    utm_term: params.get("utm_term") || undefined,
    utm_content: params.get("utm_content") || undefined,
    utm_id: params.get("utm_id") || undefined,
    gclid: params.get("gclid") || undefined,
    fbclid: params.get("fbclid") || undefined,
  };
}

// Get location info
function getLocationInfo(): LocationInfo {
  const loc = window.location;
  return {
    path: loc.pathname,
    fullUrl: loc.href,
    hash: loc.hash,
    search: loc.search,
    hostname: loc.hostname,
    protocol: loc.protocol,
    port: loc.port,
    origin: loc.origin,
  };
}

// Get document info
function getDocumentInfo(): DocumentInfo {
  return {
    title: document.title,
    characterSet: document.characterSet,
    contentType: document.contentType,
    readyState: document.readyState,
    visibilityState: document.visibilityState,
    hidden: document.hidden,
  };
}

// Get battery info (async)
async function getBatteryInfo(): Promise<BatteryInfo | undefined> {
  try {
    const nav = navigator as any;
    if (!nav.getBattery) return undefined;

    const battery = await nav.getBattery();
    return {
      charging: battery.charging,
      level: battery.level,
      chargingTime:
        battery.chargingTime === Infinity ? undefined : battery.chargingTime,
      dischargingTime:
        battery.dischargingTime === Infinity
          ? undefined
          : battery.dischargingTime,
    };
  } catch {
    return undefined;
  }
}

// Generate canvas fingerprint (hashed for privacy)
function getCanvasFingerprint(): string | undefined {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 200;
    canvas.height = 50;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    ctx.textBaseline = "top";
    ctx.font = "14px Arial";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("KoriCode 🎨", 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.fillText("Fingerprint", 4, 17);

    const dataUrl = canvas.toDataURL();
    // Simple hash
    let hash = 0;
    for (let i = 0; i < dataUrl.length; i++) {
      const char = dataUrl.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return hash.toString(36);
  } catch {
    return undefined;
  }
}

// Generate audio fingerprint (hashed)
function getAudioFingerprint(): string | undefined {
  try {
    const AudioContext =
      window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContext) return undefined;

    const context = new AudioContext();
    const oscillator = context.createOscillator();
    const analyser = context.createAnalyser();
    const gain = context.createGain();
    const processor = context.createScriptProcessor(4096, 1, 1);

    gain.gain.value = 0; // Mute
    oscillator.type = "triangle";
    oscillator.connect(analyser);
    analyser.connect(processor);
    processor.connect(gain);
    gain.connect(context.destination);

    oscillator.start(0);

    const fingerprint = context.sampleRate.toString(36);

    oscillator.stop();
    context.close();

    return fingerprint;
  } catch {
    return undefined;
  }
}

// Generate WebGL fingerprint
function getWebGLFingerprint(): string | undefined {
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl");
    if (!gl) return undefined;

    const extensions = gl.getSupportedExtensions();
    const fingerprint = extensions?.join(",") || "";

    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return hash.toString(36);
  } catch {
    return undefined;
  }
}

export default function VisitorTracker() {
  const visitorMutation = useVisitorTracking();
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    // Prevent multiple executions
    if (hasTrackedRef.current) {
      return;
    }

    const trackVisitor = async () => {
      // Check Consent
      const consent = localStorage.getItem("cookie_consent");
      if (consent !== "all") {
        console.log("🔒 Tracking skipped: User has not accepted cookies");
        hasTrackedRef.current = true;
        return;
      }

      // Check if already tracked in this session
      const hasTracked = sessionStorage.getItem("visitor_tracked");
      if (hasTracked) {
        console.log("✓ Already tracked in this session");
        hasTrackedRef.current = true;
        return;
      }

      // Mark as tracking to prevent duplicate calls
      hasTrackedRef.current = true;

      // Get visitor info
      const { visitorId, isNew, visitCount, previousVisit } =
        getOrCreateVisitorId();

      // Get battery info (async)
      const batteryInfo = await getBatteryInfo();

      // Collect comprehensive browser data
      const data: VisitorData = {
        // Basic browser info
        userAgent: navigator.userAgent,
        language: navigator.language,
        languages: navigator.languages,
        platform: navigator.platform,
        vendor: navigator.vendor,
        appName: navigator.appName,
        appVersion: navigator.appVersion,
        product: navigator.product,
        productSub: (navigator as any).productSub || "",

        // Privacy settings
        doNotTrack: navigator.doNotTrack,
        globalPrivacyControl: (navigator as any).globalPrivacyControl,
        cookiesEnabled: navigator.cookieEnabled,

        // Network/Connection
        onLine: navigator.onLine,
        connection: getConnectionInfo(),

        // Screen & Display
        screen: getScreenInfo(),
        viewport: getViewportInfo(),
        pixelRatio: window.devicePixelRatio,

        // Time & Location
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timezoneOffset: new Date().getTimezoneOffset(),
        localTime: new Date().toISOString(),

        // Hardware
        hardwareConcurrency: navigator.hardwareConcurrency || "unknown",
        deviceMemory: (navigator as any).deviceMemory || "unknown",

        // Touch/Input
        touch: getTouchInfo(),

        // Referrer & Navigation
        referrer: document.referrer || "direct",

        // Page/Document info
        location: getLocationInfo(),
        document: getDocumentInfo(),

        // Marketing
        utm: getUTMParams(),

        // Performance (wait a bit for metrics to be available)
        performance: getPerformanceInfo(),

        // Media & Browser capabilities
        mediaCapabilities: getMediaCapabilities(),

        // Battery
        battery: batteryInfo,

        // Session info
        sessionId: generateSessionId(),
        isNewVisitor: isNew,
        visitCount,
        previousVisitTimestamp: previousVisit,

        // Fingerprinting
        canvasFingerprint: getCanvasFingerprint(),
        audioFingerprint: getAudioFingerprint(),
        webglFingerprint: getWebGLFingerprint(),

        // Plugins
        pluginsCount: navigator.plugins?.length || 0,
      };

      console.log("📊 Sending comprehensive visitor data to server...", data);

      visitorMutation.mutate(data, {
        onSuccess: (result) => {
          if (result.success) {
            sessionStorage.setItem("visitor_tracked", "true");
            console.log(
              "✅ Visitor tracked successfully! Document ID:",
              result.id,
            );
          } else {
            console.error("❌ Server returned error:", result.message);
            // Reset on error so it can retry
            hasTrackedRef.current = false;
          }
        },
        onError: (error) => {
          console.error("❌ Error tracking visitor:", error);
          if (error instanceof Error) {
            console.error("Error message:", error.message);
          }
          // Reset on error so it can retry
          hasTrackedRef.current = false;
        },
      });
    };

    // Wait a bit for performance metrics to be available
    setTimeout(() => {
      trackVisitor();
    }, 1000);
  }, []);

  return null;
}
